import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { showToast } from '../layout/ToastContainer';

const EMPTY_FORM = { username: '', name: '', email: '', phone: '', password: '', confirmPassword: '' };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [pwReset, setPwReset] = useState(null); // { username, newPassword }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data.users);
    } catch {
      showToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(er => ({ ...er, [e.target.name]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.username.trim()) e.username = 'Required';
    else if (!/^[a-zA-Z0-9_-]+$/.test(form.username)) e.username = 'Letters, numbers, _ and - only';
    if (!form.password) e.password = 'Required';
    else if (form.password.length < 8) e.password = 'At least 8 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function doAdd(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/users', {
        username: form.username.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password
      });
      setUsers(prev => [...prev, data]);
      setForm(EMPTY_FORM);
      setErrors({});
      setShowAdd(false);
      showToast(`User "${data.username}" created`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create user', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function doDelete(username) {
    setDeleting(username);
    try {
      await api.delete(`/users/${username}`);
      setUsers(prev => prev.filter(u => u.username !== username));
      setConfirmDelete(null);
      showToast(`User "${username}" deleted`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to delete user', 'error');
    } finally {
      setDeleting(null);
    }
  }

  async function doResetPassword() {
    if (!pwReset?.newPassword || pwReset.newPassword.length < 8) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }
    try {
      await api.patch(`/users/${pwReset.username}/password`, { newPassword: pwReset.newPassword });
      setPwReset(null);
      showToast('Password updated', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update password', 'error');
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '0.05em' }}>
            User Management
          </h1>
          <div style={{ color: '#9aa3b0', fontSize: 13, marginTop: 4 }}>
            {users.length} user{users.length !== 1 ? 's' : ''}
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowAdd(true); setForm(EMPTY_FORM); setErrors({}); }}>
          + Add User
        </button>
      </div>

      {/* Users table */}
      <div className="card" style={{ overflow: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <div className="spinner spinner-lg" />
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.username}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--accent-orange)' }}>
                    {u.username}
                  </td>
                  <td style={{ fontWeight: 600 }}>{u.name || '—'}</td>
                  <td style={{ color: '#c8d0dc', fontFamily: 'var(--font-mono)', fontSize: 13 }}>{u.email || '—'}</td>
                  <td style={{ color: '#b8c0cc' }}>{u.phone || '—'}</td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                      <button className="btn btn-ghost btn-sm"
                        onClick={() => setPwReset({ username: u.username, newPassword: '' })}
                        style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
                        Reset PW
                      </button>
                      <button className="btn btn-danger btn-sm"
                        onClick={() => setConfirmDelete(u.username)}
                        disabled={deleting === u.username}>
                        {deleting === u.username ? '...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add User Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>Add User</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowAdd(false)}
                style={{ fontSize: 18, padding: '4px 10px' }}>✕</button>
            </div>
            <form onSubmit={doAdd}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="label">Username *</label>
                  <input className={`input-field${errors.username ? ' error' : ''}`}
                    name="username" value={form.username} onChange={handleChange}
                    placeholder="jsmith" autoComplete="off" />
                  {errors.username && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 3 }}>{errors.username}</div>}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="label">Full Name</label>
                  <input className="input-field" name="name" value={form.name} onChange={handleChange}
                    placeholder="Jane Smith" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="label">Email</label>
                  <input className="input-field" name="email" type="email" value={form.email} onChange={handleChange}
                    placeholder="jane@hdontap.com" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="label">Phone</label>
                  <input className="input-field" name="phone" value={form.phone} onChange={handleChange}
                    placeholder="858.555.0100" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="label">Password *</label>
                  <input className={`input-field${errors.password ? ' error' : ''}`}
                    name="password" type="password" value={form.password} onChange={handleChange}
                    placeholder="Min 8 characters" autoComplete="new-password" />
                  {errors.password && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 3 }}>{errors.password}</div>}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="label">Confirm Password *</label>
                  <input className={`input-field${errors.confirmPassword ? ' error' : ''}`}
                    name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange}
                    placeholder="Repeat password" autoComplete="new-password" />
                  {errors.confirmPassword && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 3 }}>{errors.confirmPassword}</div>}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {pwReset && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPwReset(null)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setPwReset(null)}
                style={{ fontSize: 18, padding: '4px 10px' }}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 14 }}>
                Setting new password for <span style={{ color: 'var(--accent-orange)', fontFamily: 'var(--font-mono)' }}>{pwReset.username}</span>
              </div>
              <input className="input-field" type="password"
                placeholder="New password (min 8 characters)"
                value={pwReset.newPassword}
                onChange={e => setPwReset(p => ({ ...p, newPassword: e.target.value }))}
                autoComplete="new-password" />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => setPwReset(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={doResetPassword}>
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
                Are you sure you want to delete user <strong style={{ color: 'var(--accent-orange)' }}>{confirmDelete}</strong>?
                This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger btn-sm"
                onClick={() => doDelete(confirmDelete)}
                disabled={deleting === confirmDelete}>
                {deleting === confirmDelete ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
